import React, { useState, useRef, useCallback, useEffect, useMemo, useContext } from 'react';
import { withTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import UIContext from 'components/Utils/UIContext';
import Item from './Item';
import Radar from './Radar';
import { Amphi } from './Amphi';
import { keyBy, flatten } from 'lodash';
import { isItemInactive } from 'utils/rules';

import('./MainRadar.sass');

export const radarOriginalSize = 560;

// Animation config
const MOVE_DURATION = 2.0; // in seconds for CSS - slower
const CSS_EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)'; // Smooth ease-out for gentle movement

// Position type
interface AnimatedPosition {
	x: number;
	y: number;
	svgX: number;
	svgY: number;
}

interface RadarItem {
	id: string;
	x: number;
	y: number;
	name: string;
	icon?: string;
	impact?: string;
	trend?: string;
	links: Array<{
		id: string;
		originId: string;
		targetId: string;
		strength: number;
	}>;
}

interface MainRadarProps {
	children: RadarItem[];
	height: number;
	width?: number;
	offsetX: number;
	offsetY: number;
	selection?: RadarItem | null;
	mode?: { name: string };
	scenario?: string;
	isLegendVisible?: boolean;
	isDecisions?: boolean;
	isTransitioning?: boolean;
	displayAllLinks?: boolean;
	recentCreations?: Record<string, boolean>;
	recentUpdates?: Record<string, boolean>;
	t: TFunction;
	select: (item: RadarItem) => void;
	deselect: (callback?: () => void) => void;
	onChange: (action: string, value: unknown) => void;
	onUpdateItem: (id: string, coords: { x: number; y: number }) => void;
	onClickLink: (link: unknown, strength: number) => void;
}

function MainRadar(props: MainRadarProps) {
	const {
		children = [],
		height,
		offsetX,
		offsetY,
		selection,
		mode,
		scenario,
		isLegendVisible,
		isDecisions,
		displayAllLinks,
		recentCreations = {},
		recentUpdates = {},
		t,
		select,
		deselect,
		onChange,
		onUpdateItem,
		onClickLink
	} = props;

	const uiContext = useContext(UIContext);
	const canEdit = uiContext.canEdit;

	const radar = useRef(new Radar()).current;
	const [isAmphiVisible, setIsAmphiVisible] = useState(false);
	const [isResizing, setIsResizing] = useState(false);
	const [dragState, setDragState] = useState<{
		itemId: string | null;
		mouseXY: [number, number];
		delta: [number, number];
	}>({
		itemId: null,
		mouseXY: [0, 0],
		delta: [0, 0]
	});

	// Use a ref to always have the current dragState value (avoids stale closure issues)
	const dragStateRef = useRef(dragState);
	dragStateRef.current = dragState;

	const resizingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const startDragAt = useRef<number | null>(null);
	const hasMoved = useRef(false);
	const prevScenario = useRef(scenario);
	const prevDimensions = useRef({ width: props.width, height });

	// Track dropped positions temporarily until data is synchronized
	const [droppedPositions, setDroppedPositions] = useState<Record<string, { x: number; y: number }>>({});


	// Staggered entrance indices for wave effect (only for truly new items)
	const [entranceIndices, setEntranceIndices] = useState<Record<string, number>>({});
	const prevChildrenRef = useRef<RadarItem[]>([]);
	const isFirstRender = useRef(true);
	// Keep track of cleanup timeouts separately so they don't get cancelled
	const cleanupTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			cleanupTimeoutsRef.current.forEach(t => clearTimeout(t));
			cleanupTimeoutsRef.current.clear();
		};
	}, []);

	// Track entering items
	useEffect(() => {
		const currentIds = new Set(children.map(c => c.id));
		const prevIds = new Set(prevChildrenRef.current.map(c => c.id));

		// Skip wave effect on first render only
		if (isFirstRender.current) {
			isFirstRender.current = false;
			prevChildrenRef.current = children;
			return;
		}

		// Find new items (entering) - items that appear and weren't visible before
		const newItems: Record<string, number> = {};
		let enterIndex = 0;
		currentIds.forEach(id => {
			if (!prevIds.has(id)) {
				newItems[id] = enterIndex++;
			}
		});

		// Update entrance indices for truly new items
		if (Object.keys(newItems).length > 0) {
			setEntranceIndices(prev => ({ ...prev, ...newItems }));
			const entranceTimeout = setTimeout(() => {
				setEntranceIndices(prev => {
					const updated = { ...prev };
					Object.keys(newItems).forEach(id => delete updated[id]);
					return updated;
				});
				cleanupTimeoutsRef.current.delete(entranceTimeout);
				// Must outlast the longest CSS animation on `.ItemWrapper.entering`:
				// ripple-out runs 7s and the last wave starts after a 3s delay
				// (see Item.sass), so keep the class for ~10.5s.
			}, 10500 + Object.keys(newItems).length * 50);
			cleanupTimeoutsRef.current.add(entranceTimeout);
		}

		prevChildrenRef.current = children;
	}, [children]);

	// Handle scenario change
	useEffect(() => {
		if (scenario !== prevScenario.current) {
			setIsAmphiVisible(false);
			setDroppedPositions({});
			prevScenario.current = scenario;
		}
	}, [scenario]);

	// Clear dropped positions when children data changes (positions synced from server)
	useEffect(() => {
		if (Object.keys(droppedPositions).length === 0) return;

		// Check if any dropped item's position has been updated in children
		const updatedIds: string[] = [];
		Object.keys(droppedPositions).forEach(id => {
			const child = children.find(c => c.id === id);
			if (child) {
				// Compare with the new calculated position
				const newPos = radar.denormalizeCoordinates(height, height, 0, 0, child.x, child.y);
				const droppedPos = droppedPositions[id];
				// If the positions are close enough, the data has been synced
				const dx = Math.abs(newPos[0] - droppedPos.x);
				const dy = Math.abs(newPos[1] - droppedPos.y);
				if (dx < 5 && dy < 5) {
					updatedIds.push(id);
				}
			}
		});

		if (updatedIds.length > 0) {
			setDroppedPositions(prev => {
				const updated = { ...prev };
				updatedIds.forEach(id => delete updated[id]);
				return updated;
			});
		}
	}, [children, droppedPositions, radar, height]);

	// Handle resize
	useEffect(() => {
		if (props.width !== prevDimensions.current.width || height !== prevDimensions.current.height) {
			setIsResizing(true);
			if (resizingTimer.current) clearTimeout(resizingTimer.current);
			resizingTimer.current = setTimeout(() => setIsResizing(false), 200);
			prevDimensions.current = { width: props.width, height };
		}
	}, [props.width, height]);

	// Cleanup
	useEffect(() => {
		return () => {
			if (resizingTimer.current) clearTimeout(resizingTimer.current);
		};
	}, []);

	const handleMouseDown = useCallback((index: number, e: React.MouseEvent | React.TouchEvent) => {
		const item = children[index];
		select(item);

		// Lecture seule : on autorise la sélection (lecture) mais pas le drag
		// qui déplacerait l'item (mutation impact/tendance).
		if (!canEdit) return;

		if (!mode || mode.name !== 'initial') return;

		// Use clientX/clientY consistently for both mouse and touch
		const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
		const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

		// Use the VISUAL position of the item (droppedPositions if exists, otherwise calculate)
		const droppedPos = droppedPositions[item.id];
		let itemX: number, itemY: number;
		if (droppedPos) {
			// Use the temporary dropped position (not yet synced to server)
			itemX = droppedPos.x;
			itemY = droppedPos.y;
		} else {
			// Calculate from item coordinates (same as positions useMemo)
			[itemX, itemY] = radar.denormalizeCoordinates(height, height, 0, 0, item.x, item.y);
		}

		// Delta is calculated so that: itemPosition = mousePosition + delta - offset
		// Therefore: delta = itemPosition + offset - mousePosition
		const deltaX = itemX + offsetX - clientX;
		const deltaY = itemY + offsetY - clientY;

		hasMoved.current = false;
		startDragAt.current = Date.now();

		setDragState({
			itemId: item.id,
			mouseXY: [clientX, clientY],
			delta: [deltaX, deltaY]
		});
	}, [children, select, mode, radar, height, offsetX, offsetY, droppedPositions, canEdit]);

	const handleMouseMove = useCallback((e: MouseEvent | TouchEvent) => {
		// Use ref to get current value (avoids stale closure)
		if (!dragStateRef.current.itemId) return;

		// Use clientX/clientY consistently
		const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
		const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

		hasMoved.current = true;
		setDragState(prev => {
			// Skip update if position hasn't changed significantly (reduces re-renders)
			if (Math.abs(prev.mouseXY[0] - clientX) < 1 && Math.abs(prev.mouseXY[1] - clientY) < 1) {
				return prev;
			}
			return {
				...prev,
				mouseXY: [clientX, clientY]
			};
		});
		// Only update if currently visible (avoids unnecessary state updates)
		if (isAmphiVisible) {
			setIsAmphiVisible(false);
		}
	}, [isAmphiVisible]);

	const handleMouseUp = useCallback((e: MouseEvent | TouchEvent) => {
		// Use ref to get current value (avoids stale closure)
		const currentDragState = dragStateRef.current;
		const timeDiff = startDragAt.current ? Date.now() - startDragAt.current : 0;

		if (!hasMoved.current || timeDiff < 250) {
			setDragState({ itemId: null, mouseXY: [0, 0], delta: [0, 0] });
			return;
		}

		if (currentDragState.itemId) {
			// Use clientX/clientY consistently
			const clientX = 'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;
			const clientY = 'changedTouches' in e ? e.changedTouches[0].clientY : e.clientY;

			// Calculate the visual position of the item center (relative to radar)
			let visualX = clientX + currentDragState.delta[0] - offsetX;
			let visualY = clientY + currentDragState.delta[1] - offsetY;

			// Snap to Point of View center if dropped within its zone (SVG circle at 510,280 r=32)
			const ratio = radarOriginalSize / height;
			const dropSvgX = visualX * ratio;
			const dropSvgY = visualY * ratio;
			const povDx = dropSvgX - 510;
			const povDy = dropSvgY - 280;
			if (povDx * povDx + povDy * povDy <= 32 * 32) {
				visualX = 510 / ratio;
				visualY = 280 / ratio;
			}

			// Normalize from the visual item center so saved coords match the drop position
			let coords = radar.normalizeCoordinates(
				height, height, offsetX, offsetY,
				visualX + offsetX, visualY + offsetY
			);
			coords[0] = Math.max(-125, Math.min(125, coords[0]));
			coords[1] = Math.max(-125, Math.min(125, coords[1]));

			const item = children.find(c => c.id === currentDragState.itemId);
			if (item) {
				// Store the visual drop position to prevent jumping back
				setDroppedPositions(prev => ({
					...prev,
					[item.id]: { x: visualX, y: visualY }
				}));

				onUpdateItem(item.id, { x: coords[0], y: coords[1] });
			}
		}

		hasMoved.current = false;
		startDragAt.current = null;
		setDragState({ itemId: null, mouseXY: [0, 0], delta: [0, 0] });
	}, [radar, height, offsetX, offsetY, children, onUpdateItem]);

	const handleDoubleClick = useCallback(() => {
		if (!canEdit) return;
		setIsAmphiVisible(!isAmphiVisible && (!mode || mode.name !== 'linking'));
		setDragState({ itemId: null, mouseXY: [0, 0], delta: [0, 0] });
	}, [isAmphiVisible, mode, canEdit]);

	const handleTouchStart = useCallback((index: number, e: React.TouchEvent) => {
		if (isAmphiVisible) {
			setIsAmphiVisible(false);
		} else {
			handleMouseDown(index, e);
		}
	}, [isAmphiVisible, handleMouseDown]);

	// Event listeners
	useEffect(() => {
		const onMouseMove = (e: MouseEvent) => handleMouseMove(e);
		const onMouseUp = (e: MouseEvent) => handleMouseUp(e);
		const onTouchMove = (e: TouchEvent) => {
			e.preventDefault();
			handleMouseMove(e);
		};
		const onTouchEnd = (e: TouchEvent) => handleMouseUp(e);

		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);
		window.addEventListener('touchmove', onTouchMove, { passive: false });
		window.addEventListener('touchend', onTouchEnd);

		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
			window.removeEventListener('touchmove', onTouchMove);
			window.removeEventListener('touchend', onTouchEnd);
		};
	}, [handleMouseMove, handleMouseUp]);

	// Compute values
	const itemsCount = children.length;
	const itemScale = radar.item_scale(itemsCount);
	const ratio = radarOriginalSize / height;

	// Index children for quick lookup
	const indexedChildren = useMemo(() => keyBy(children, 'id'), [children]);


	// Compute TARGET positions for all items
	const positions = useMemo(() => {
		const pos: Record<string, AnimatedPosition> = {};
		children.forEach(c => {
			const coords = radar.denormalizeCoordinates(height, height, 0, 0, c.x, c.y);
			pos[c.id] = {
				x: coords[0],
				y: coords[1],
				svgX: coords[0] * ratio,
				svgY: coords[1] * ratio
			};
		});
		return pos;
	}, [children, radar, height, ratio]);

	// Detect if a stakeholder occupies the Point of View zone
	const isPovOccupied = useMemo(() => {
		const POV_R_SQ = 32 * 32;
		return children.some(c => {
			const dropped = droppedPositions[c.id];
			let svgX: number, svgY: number;
			if (dropped) {
				svgX = dropped.x * ratio;
				svgY = dropped.y * ratio;
			} else {
				const pos = positions[c.id];
				if (!pos) return false;
				svgX = pos.svgX;
				svgY = pos.svgY;
			}
			const dx = svgX - 510;
			const dy = svgY - 280;
			return dx * dx + dy * dy <= POV_R_SQ;
		});
	}, [children, positions, droppedPositions, ratio]);

	// CSS transition string - browser-optimized
	const cssTransition = `transform ${MOVE_DURATION}s ${CSS_EASING}`;

	// Refs for DOM elements
	const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const linkGroupRef = useRef<SVGGElement | null>(null);
	const animFrameRef = useRef<number | null>(null);

	// Sync links with item positions during CSS animation
	useEffect(() => {
		// Parse transform matrix to get current animated position
		const getTranslate = (el: HTMLDivElement): [number, number] => {
			// Always use getComputedStyle to get the current animated value
			const style = getComputedStyle(el);
			const transform = style.transform;
			if (!transform || transform === 'none') return [0, 0];
			const match = transform.match(/matrix\(([^)]+)\)/);
			if (match) {
				const values = match[1].split(',').map(v => parseFloat(v.trim()));
				return [values[4] || 0, values[5] || 0];
			}
			return [0, 0];
		};

		const syncLinks = () => {
			if (!linkGroupRef.current) return;

			const lines = linkGroupRef.current.querySelectorAll('g[data-link-id]');
			lines.forEach(g => {
				const originId = g.getAttribute('data-origin-id');
				const targetId = g.getAttribute('data-target-id');
				if (!originId || !targetId) return;

				const originEl = itemRefs.current[originId];
				const targetEl = itemRefs.current[targetId];
				if (!originEl || !targetEl) return;

				const [ox, oy] = getTranslate(originEl);
				const [tx, ty] = getTranslate(targetEl);

				// Convert to SVG coordinates
				const x1 = ox * ratio;
				const y1 = oy * ratio;
				const x2 = tx * ratio;
				const y2 = ty * ratio;

				const lineEls = g.querySelectorAll('line');
				lineEls.forEach(line => {
					line.setAttribute('x1', String(x1));
					line.setAttribute('y1', String(y1));
					line.setAttribute('x2', String(x2));
					line.setAttribute('y2', String(y2));
				});
			});

			animFrameRef.current = requestAnimationFrame(syncLinks);
		};

		// Start sync loop
		animFrameRef.current = requestAnimationFrame(syncLinks);

		return () => {
			if (animFrameRef.current) {
				cancelAnimationFrame(animFrameRef.current);
			}
		};
	}, [ratio]);

	// Highlighted items
	const highlightedItems = useMemo(() => {
		const highlighted: Record<string, boolean> = {};
		if (selection) {
			highlighted[selection.id] = true;
			const links = flatten(children.map(c => c.links)).filter(
				l => l.originId === selection.id || l.targetId === selection.id
			);
			links.forEach(l => {
				highlighted[l.targetId] = true;
				highlighted[l.originId] = true;
			});
		}
		return highlighted;
	}, [selection, children]);

	// Target links - only show links where BOTH endpoints are in current children
	const targetLinks = useMemo(() => {
		const currentChildrenIds = new Set(children.map(c => c.id));
		const allLinks = flatten(children.map(c => c.links));

		// Filter to only links where both endpoints exist in current children
		const validLinks = allLinks.filter(l =>
			currentChildrenIds.has(l.originId) && currentChildrenIds.has(l.targetId)
		);

		if (selection && currentChildrenIds.has(selection.id)) {
			return validLinks.filter(
				l => l.originId === selection.id || l.targetId === selection.id
			);
		}
		return displayAllLinks ? validLinks : [];
	}, [selection, displayAllLinks, children]);

	// Amphi coordinates
	const amphiCoordinates = selection
		? radar.denormalizeCoordinates(height, height, 0, 0, selection.x, selection.y)
		: [null, null];

	const handleAmphiClick = useCallback((action: string, value: unknown) => {
		onChange(action, value);
		setIsAmphiVisible(false);
	}, [onChange]);

	return (
		<div
			id="MainRadar"
			className="noselection"
			style={{ width: height + 'px', height: height + 'px' }}
		>
			<Amphi
				key={selection?.id || 'empty'}
				x={amphiCoordinates[0]}
				y={amphiCoordinates[1]}
				visible={isAmphiVisible}
				onChange={handleAmphiClick}
				onDismiss={() => setIsAmphiVisible(false)}
				selection={selection}
			/>

			<svg
				id="vectorialRadar"
				viewBox="0 0 560 560"
				width={height + 'px'}
				height={height + 'px'}
				onClick={() => deselect(() => setIsAmphiVisible(false))}
			>
				<defs>
					<mask id="positive">
						<rect width="100%" height="50%" x="0" y="0" fill="white" />
					</mask>
					<mask id="negative">
						<rect width="100%" height="50%" x="0" y="280" fill="white" />
					</mask>

					<path id="externalTopPath" d="M115,222 a115,115 0 0,1 330,0" />
					<path id="externalBottomPath" d="M115,388 a115,115 0 0,0 330,0" />
					<path id="internalTopPath" d="M115,280 a115,115 0 0,1 330,0" />
					<path id="internalBottomPath" d="M115,280 a115,115 0 0,0 330,0" />

					<linearGradient id="greenToRed" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="1">
						<stop offset="49.49999%" style={{ stopColor: '#24CCB8' }} />
						<stop offset="49.49999%" style={{ stopColor: '#E70549' }} />
					</linearGradient>

					{/* Masque pour créer l'écart de la zone neutre */}
					<mask id="horizonGap">
						<rect width="100%" height="100%" fill="white" />
						<rect x="0" y="261" width="100%" height="39" fill="black" />
					</mask>
				</defs>

				{isLegendVisible && (
					<g className="Legend noselection" transform="translate(50, 0)">
						<g className="positive" transform={`translate(-${560 / 2 + 20}, ${560 / 2}) rotate(-90 0 0)`}>
							<text x="20" y={560 / 2 - 35}>{t('pages.navigator.zones.positive')}</text>
							<text x="20" y={560 / 2 + 2}>{t('pages.navigator.zones.positive_line2')}</text>
						</g>
						<g className="neutral" transform={`translate(-166, 46)`}>
							<text x="80" y={560 / 2 - 40}>{t('pages.navigator.zones.neutral')}</text>
						</g>
						<g className="negative" transform={`translate(-${560 / 2 + 20}, ${560 / 2}) rotate(-90 0 0)`}>
							<text x="-20" y={560 / 2 - 35}>{t('pages.navigator.zones.negative')}</text>
							<text x="-20" y={560 / 2 + 2}>{t('pages.navigator.zones.negative_line2')}</text>
						</g>
					</g>
				)}

				<g mask="url(#horizonGap)">
					<circle className="circle2" r="165" cx="280" cy="280" />
					<circle className="circle1" r="100" cx="280" cy="280" />
				</g>

				<g className="circle3" mask="url(#horizonGap)">
					<g transform={`scale(${560 / 747}) translate(${747 / 2}, ${747 / 2})`}>
						<path
							strokeWidth="0"
							strokeOpacity={0}
							fill="url(#greenToRed)"
							d="M308,3c0-165.8-132.3-300.6-297.1-304.9c0,0,0,0,0,0c0,0-7.9-10.3-7.9-10.3s-6.7,9-7.9,10.3
			c0,0,0,0,0,0C-169.7-297.6-302-162.8-302,3c0,165.8,132.3,300.7,297.1,304.9L3,318.2l7.9-10.3C175.7,303.7,308,168.8,308,3z
			 M9.4,304.4l-2.7,3.5l0,0l-3.6,4.8l-3.6-4.8l0,0l-2.7-3.5C-166.9,301-298.5,167.4-298.5,3S-167-295-3.4-298.4l2.7-3.5l0,0l3.7-4.8
							l3.7,4.8l0,0l2.7,3.5C173-295,304.5-161.4,304.5,3S172.9,301,9.4,304.4z"
						/>
					</g>
				</g>

				<text dy="-18px" style={{ fill: 'rgba(255,255,255,0.14)' }} letterSpacing="1px">
					<textPath className="noselection" xlinkHref="#externalBottomPath" startOffset="50%">
						{t('models.item.activeness_zones.external_inner_negative')}
					</textPath>
				</text>
				<text dy="-21.5px" style={{ fill: 'rgba(255,255,255,0.14)' }} letterSpacing="-0.6px">
					<textPath className="noselection" xlinkHref="#externalTopPath" startOffset="50%">
						{t('models.item.activeness_zones.external_inner_positive')}
					</textPath>
				</text>
				<text dy="-21.5px" style={{ fill: 'rgba(255,255,255,0.14)' }} letterSpacing="-0.6px">
					<textPath className="noselection" xlinkHref="#internalTopPath" startOffset="50%">
						{t('models.item.activeness_zones.internal_low_positive')}
					</textPath>
				</text>
				<text dy="39.5px" style={{ fill: 'rgba(255,255,255,0.15)' }} letterSpacing="2.5px">
					<textPath className="noselection" xlinkHref="#internalTopPath" startOffset="50%">
						{t('models.item.activeness_zones.internal_medium_positive')}
					</textPath>
				</text>
				<text className="noselection" dy={isDecisions ? '-40' : '-20'} x="280" y="280" style={{ fill: 'rgba(255,255,255,0.16)' }}>
					{t('models.item.activeness_zones.internal_high_positive')}
				</text>
				<text dy="35px" style={{ fill: 'rgba(255,255,255,0.14)' }} letterSpacing="-0.7px">
					<textPath className="noselection" xlinkHref="#internalBottomPath" startOffset="50%">
						{t('models.item.activeness_zones.internal_low_negative')}
					</textPath>
				</text>
				<text dy="-26.5px" style={{ fill: 'rgba(255,255,255,0.15)' }} letterSpacing="3px">
					<textPath className="noselection" xlinkHref="#internalBottomPath" startOffset="50%" letterSpacing="2.4px">
						{t('models.item.activeness_zones.internal_medium_negative')}
					</textPath>
				</text>
				<text className="noselection" dy={isDecisions ? '50' : '30'} x="280" y="280" style={{ fill: 'rgba(255,255,255,0.16)' }}>
					{t('models.item.activeness_zones.internal_high_negative')}
				</text>

				{/* Horizon - zone neutre (bande large semi-transparente) */}
				<rect className="horizon-band" x="40" y="270" width="450" height="20" />

				{/* Point of View */}
				<g className={`point-of-view${isPovOccupied ? ' pov-occupied' : ''}`} transform="translate(510, 280)">
					<circle r="32" className="pov-ring pov-ring-outer" />
					<circle r="25" className="pov-ring pov-ring-middle" />
					<circle r="18" className="pov-ring pov-ring-inner" />
					<text className="pov-text noselection" textAnchor="middle" dominantBaseline="central">
						<tspan x="0" dy="-8">{t('pages.navigator.zones.point_of_view_line1')}</tspan>
						<tspan x="0" dy="8">{t('pages.navigator.zones.point_of_view_line2')}</tspan>
						<tspan x="0" dy="8">{t('pages.navigator.zones.point_of_view_line3')}</tspan>
					</text>
				</g>
			</svg>

			{/* Links SVG overlay - synced with items via requestAnimationFrame */}
			<svg
				className="linksOverlay"
				viewBox="0 0 560 560"
				width={height + 'px'}
				height={height + 'px'}
				style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 1 }}
			>
				<g ref={linkGroupRef}>
					{targetLinks.map((l) => {
						const origin = positions[l.originId];
						const target = positions[l.targetId];
						if (!origin || !target) return null;

						// Links are only rendered if both endpoints exist (filtered in targetLinks useMemo)
						const isExiting = false;

						// Check if either endpoint is a new item (entering)
						const isOriginEntering = entranceIndices[l.originId] !== undefined;
						const isTargetEntering = entranceIndices[l.targetId] !== undefined;
						const isLinkEntering = isOriginEntering || isTargetEntering;
						// Delay link appearance after item appears
						const linkEntranceDelay = isLinkEntering
							? 0.5 + Math.max(entranceIndices[l.originId] ?? 0, entranceIndices[l.targetId] ?? 0) * 0.04
							: 0;

						// Initial positions (will be updated by syncLinks)
						let x1 = origin.svgX;
						let y1 = origin.svgY;
						let x2 = target.svgX;
						let y2 = target.svgY;

						const isDraggingOrigin = dragState.itemId === l.originId;
						const isDraggingTarget = dragState.itemId === l.targetId;
						const originDropped = droppedPositions[l.originId];
						const targetDropped = droppedPositions[l.targetId];

						if (isDraggingOrigin) {
							const dragX = dragState.mouseXY[0] + dragState.delta[0] - offsetX;
							const dragY = dragState.mouseXY[1] + dragState.delta[1] - offsetY;
							x1 = dragX * ratio;
							y1 = dragY * ratio;
						} else if (originDropped) {
							x1 = originDropped.x * ratio;
							y1 = originDropped.y * ratio;
						}
						if (isDraggingTarget) {
							const dragX = dragState.mouseXY[0] + dragState.delta[0] - offsetX;
							const dragY = dragState.mouseXY[1] + dragState.delta[1] - offsetY;
							x2 = dragX * ratio;
							y2 = dragY * ratio;
						} else if (targetDropped) {
							x2 = targetDropped.x * ratio;
							y2 = targetDropped.y * ratio;
						}

						const originNormalized = [indexedChildren[l.originId].x, indexedChildren[l.originId].y];
						const isInactive = isItemInactive({ x: originNormalized[0], y: originNormalized[1] });

						return (
							<g
								key={l.id}
								data-link-id={l.id}
								data-origin-id={l.originId}
								data-target-id={l.targetId}
								style={{
									pointerEvents: isExiting ? 'none' : 'auto',
									opacity: isExiting ? 0 : isLinkEntering ? 0 : 1,
									transition: isLinkEntering
										? `opacity 0.4s ease-out ${linkEntranceDelay}s`
										: 'opacity 0.3s ease-out'
								}}
								ref={(el) => {
									// Trigger link entrance animation after item appears
									if (el && isLinkEntering) {
										requestAnimationFrame(() => {
											el.style.opacity = '1';
										});
									}
								}}
							>
								<line
									className={[
										'linkBackground',
										originNormalized[1] > 5 ? 'positive' : '',
										originNormalized[1] < -5 ? 'negative' : '',
										originNormalized[1] <= 5 && originNormalized[1] >= -5 ? 'neutral' : '',
										isResizing ? 'resizing' : ''
									].join(' ')}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										if (!canEdit) return;
										onClickLink(l, l.strength >= 3 ? 1 : l.strength + 1);
									}}
									onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
									strokeWidth={l.strength}
									x1={x1}
									y1={y1}
									x2={x2}
									y2={y2}
								/>
								<line
									className={[
										'link',
										originNormalized[1] > 5 && !isInactive ? 'positive' : '',
										originNormalized[1] < -5 && !isInactive ? 'negative' : '',
										(isInactive || (originNormalized[1] <= 5 && originNormalized[1] >= -5)) ? 'neutral' : '',
										isResizing ? 'resizing' : ''
									].join(' ')}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										if (!canEdit) return;
										onClickLink(l, l.strength >= 3 ? 1 : l.strength + 1);
									}}
									onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
									strokeWidth={l.strength}
									strokeDasharray={['', '2, 8', '5, 4', '10 1'][l.strength]}
									x1={x1}
									y1={y1}
									x2={x2}
									y2={y2}
								/>
							</g>
						);
					})}
				</g>
			</svg>

			{/* Items container */}
			<div className="Items" style={{ height: '0px', width: '0px' }}>
				{children.map((c, index) => {
					const isSelected = c.id === selection?.id;
					const isDragging = c.id === dragState.itemId;
					const isEntering = entranceIndices[c.id] !== undefined;
					const droppedPos = droppedPositions[c.id];

					// Calculate position
					let x: number, y: number;
					if (isDragging) {
						x = dragState.mouseXY[0] + dragState.delta[0] - offsetX;
						y = dragState.mouseXY[1] + dragState.delta[1] - offsetY;
					} else if (droppedPos) {
						// Use the dropped position temporarily until data syncs
						x = droppedPos.x;
						y = droppedPos.y;
					} else {
						const pos = positions[c.id];
						x = pos?.x ?? 0;
						y = pos?.y ?? 0;
					}

					// Disable transition during drag, resize, or when using dropped position
					const disableItemTransition = isDragging || isResizing || !!droppedPos;
					const scale = isDragging ? itemScale * 1.2 : itemScale;

					// Position transform - scale/opacity for entering items is handled by CSS animation in Item.sass
					const itemTransform = `translate(${x}px, ${y}px) scale(${scale})`;

					return (
						<div
							key={c.id}
							ref={(el) => {
								itemRefs.current[c.id] = el;
							}}
							style={{
								position: 'absolute',
								left: 0,
								top: 0,
								transform: itemTransform,
								transformOrigin: '0 0',
								zIndex: isDragging ? 99 : 1,
								transition: disableItemTransition ? 'none' : cssTransition
							}}
							className={isEntering ? 'item-entering' : ''}
						>
							<Item
								{...c}
								selected={isSelected || isDragging}
								isResizing={isResizing}
								isExternallyCreated={recentCreations[c.id]}
								isExternallyUpdated={recentUpdates[c.id]}
								isEntering={isEntering}
								fade={!!selection && !highlightedItems[c.id]}
								onMouseDown={(e: React.MouseEvent) => handleMouseDown(index, e)}
								onTouchStart={(e: React.TouchEvent) => handleTouchStart(index, e)}
								onDoubleClick={handleDoubleClick}
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default withTranslation()(MainRadar);
